import { type RefObject } from 'react'
import { Combobox as ComboboxPrimitive } from '@base-ui/react/combobox'
import { CheckIcon, ChevronDownIcon, SearchIcon, XIcon } from 'lucide-react'

import { cn } from '@/shared/lib/cn'
import { Input } from '@/shared/ui/base/input'
import { Separator } from '@/shared/ui/base/separator'

const Combobox = ComboboxPrimitive.Root

function ComboboxIcon(props: ComboboxPrimitive.Icon.Props) {
  return <ComboboxPrimitive.Icon data-slot="combobox-icon" {...props} />
}

function ComboboxInput({
  className,
  ...props
}: ComboboxPrimitive.Input.Props & {
  ref?: RefObject<HTMLInputElement | null>
}) {
  return (
    <div className="relative w-full">
      <ComboboxPrimitive.Input
        data-slot="combobox-input"
        render={<Input />}
        className={cn('pr-8', className)}
        {...props}
      />
      {/* Лупа отличает поле подбора от обычного поля ввода. */}
      <SearchIcon
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 right-2 my-auto size-4 [color:var(--control-field-placeholder)]"
      />
    </div>
  )
}

/**
 * Поле подбора внутри раскрытого списка. Прилипает к верху: список прокручивается целиком,
 * иначе поле уехало бы за край на первой же прокрутке.
 */
function ComboboxSearch(
  props: ComboboxPrimitive.Input.Props & {
    ref?: RefObject<HTMLInputElement | null>
  }
) {
  return (
    <div data-slot="combobox-search" className="sticky -top-2 z-10 -mt-2 bg-popover px-2 pt-2 pb-2">
      <ComboboxInput {...props} />
    </div>
  )
}

function ComboboxTrigger({ children, className, ...props }: ComboboxPrimitive.Trigger.Props) {
  return (
    <ComboboxPrimitive.Trigger
      data-slot="combobox-trigger"
      // Вид поля, а не кнопки: рядом стоят Select и Input, и список выбора должен читаться так же.
      className={cn(
        'flex items-center justify-between gap-2 whitespace-nowrap border bg-card',
        '[border-color:var(--control-field-border)] [border-radius:var(--control-field-radius)] [padding:var(--control-field-select-padding)] [height:var(--control-field-height)]',
        '[font-family:var(--control-font-family)] [font-size:var(--control-field-font-size)] [font-weight:var(--control-field-font-weight)] [line-height:var(--control-field-line-height)]',
        'group cursor-pointer shadow-none transition-[color,border-color,box-shadow] outline-none data-[placeholder]:[color:var(--control-field-placeholder)] data-[placeholder]:[opacity:var(--control-field-placeholder-opacity)]',
        'hover:[border-color:var(--control-field-hover-border)] focus-visible:[border-color:var(--control-field-focus-border)] focus-visible:[box-shadow:var(--control-field-focus-shadow)]',
        'disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground disabled:opacity-100',
        className
      )}
      {...props}
    >
      {children}
      {/* Признак открытого списка Base UI ставит на триггер, поэтому разворот стрелки берём с него. */}
      <ComboboxIcon className="text-muted-foreground transition-transform group-data-[popup-open]:rotate-180">
        <ChevronDownIcon className="size-4" />
      </ComboboxIcon>
    </ComboboxPrimitive.Trigger>
  )
}

function ComboboxClear({ children, className, ...props }: ComboboxPrimitive.Clear.Props) {
  return (
    <ComboboxPrimitive.Clear
      className={cn('flex h-9 w-6 items-center justify-center rounded bg-transparent p-0', className)}
      aria-label="Очистить выбор"
      data-slot="combobox-clear"
      {...props}
    >
      {children ?? <XIcon className="size-4" />}
    </ComboboxPrimitive.Clear>
  )
}

function ComboboxValue(props: ComboboxPrimitive.Value.Props) {
  return <ComboboxPrimitive.Value data-slot="combobox-value" {...props} />
}

function ComboboxChips({
  className,
  ...props
}: ComboboxPrimitive.Chips.Props & { ref?: RefObject<HTMLDivElement | null> }) {
  return (
    <ComboboxPrimitive.Chips
      data-slot="combobox-chips"
      className={cn(
        'border-input bg-card min-h-9 flex flex-wrap items-start gap-1 rounded-lg border px-1.5 py-1.5 shadow-none transition-[color,box-shadow] has-[:disabled]:cursor-not-allowed has-[:disabled]:bg-muted has-[:disabled]:text-muted-foreground has-[:disabled]:opacity-100',
        'focus-within:border-ring focus-within:ring-ring/20 focus-within:ring-2',
        className
      )}
      {...props}
    />
  )
}

function ComboboxChip({ className, ...props }: ComboboxPrimitive.Chip.Props) {
  return (
    <ComboboxPrimitive.Chip
      data-slot="combobox-chip"
      className={cn(
        'flex items-center gap-1 rounded-md bg-muted px-1 ps-2 pe-0 [font-size:var(--type-caption-font-size)] [font-weight:var(--type-caption-font-weight)] [line-height:var(--type-caption-line-height)] outline-none cursor-default focus-within:bg-primary focus-within:text-primary-foreground',
        className
      )}
      {...props}
    />
  )
}

function ComboboxChipRemove({ className, children, ...props }: ComboboxPrimitive.ChipRemove.Props) {
  return (
    <ComboboxPrimitive.ChipRemove
      data-slot="combobox-chip-remove"
      className={cn('rounded-md p-1 text-inherit hover:bg-accent-foreground/10', className)}
      aria-label="Удалить"
      {...props}
    >
      {children ?? <XIcon className="size-3.5" />}
    </ComboboxPrimitive.ChipRemove>
  )
}

function ComboboxPopup({ className, ...props }: ComboboxPrimitive.Popup.Props) {
  return (
    <ComboboxPrimitive.Popup
      data-slot="combobox-popup"
      className={cn(
        'w-(--anchor-width) max-h-[min(var(--available-height),23rem)] max-w-(--available-width) origin-(--transform-origin) overflow-y-auto scroll-pt-2 scroll-pb-2 overscroll-contain rounded-lg border bg-popover py-2 shadow-layer outline-none transition-[transform,scale,opacity] data-[ending-style]:scale-95 data-[ending-style]:opacity-0 data-[side=none]:data-[ending-style]:transition-none data-[starting-style]:scale-95 data-[starting-style]:opacity-0 data-[side=none]:data-[starting-style]:scale-100 data-[side=none]:data-[starting-style]:opacity-100 data-[side=none]:data-[starting-style]:transition-none',
        className
      )}
      {...props}
    />
  )
}

function ComboboxPositioner({ className, ...props }: ComboboxPrimitive.Positioner.Props) {
  return (
    <ComboboxPrimitive.Portal>
      <ComboboxPrimitive.Positioner
        data-slot="combobox-positioner"
        className={cn('z-50 outline-none', className)}
        {...props}
      />
    </ComboboxPrimitive.Portal>
  )
}

function ComboboxArrow(props: ComboboxPrimitive.Arrow.Props) {
  return <ComboboxPrimitive.Arrow data-slot="combobox-arrow" {...props} />
}

function ComboboxStatus({ className, ...props }: ComboboxPrimitive.Status.Props) {
  return (
    <ComboboxPrimitive.Status
      data-slot="combobox-status"
      className={cn(
        'px-4.5 py-2 text-muted-foreground [font-size:var(--type-supporting-font-size)] [font-weight:var(--type-supporting-font-weight)] [line-height:var(--type-supporting-line-height)] empty:m-0 empty:p-0',
        className
      )}
      {...props}
    />
  )
}

function ComboboxEmpty({ className, ...props }: ComboboxPrimitive.Empty.Props) {
  return (
    <ComboboxPrimitive.Empty
      data-slot="combobox-empty"
      className={cn(
        'flex items-center justify-center text-muted-foreground [font-size:var(--type-supporting-font-size)] [font-weight:var(--type-supporting-font-weight)] [line-height:var(--type-supporting-line-height)] not-empty:py-1',
        className
      )}
      {...props}
    />
  )
}

function ComboboxList(props: ComboboxPrimitive.List.Props) {
  return <ComboboxPrimitive.List data-slot="combobox-list" {...props} />
}

function ComboboxRow(props: ComboboxPrimitive.Row.Props) {
  return <ComboboxPrimitive.Row data-slot="combobox-row" {...props} />
}

function ComboboxItem({ className, ...props }: ComboboxPrimitive.Item.Props) {
  return (
    <ComboboxPrimitive.Item
      data-slot="combobox-item"
      className={cn(
        // Место под отметку выбранного пункта отведено отступом слева, а сама отметка вынута из
        // потока: у невыбранных пунктов Base UI её не рисует вовсе, и в общем ряду содержимое
        // пункта сдвигалось бы влево. Отступ работает одинаково и для разметки, и для голого
        // текста — списки-подсказки в проекте пишут и так, и так.
        // Детям разрешено сжиматься (min-w-0): без этого длинная подпись на узком экране
        // переносится по словам вместо обрезки.
        'relative [&>*]:min-w-0',
        'flex cursor-pointer items-center gap-2 py-2 pr-8 pl-[2.45rem] [font-size:var(--type-supporting-font-size)] [font-weight:var(--type-supporting-font-weight)] [line-height:var(--type-supporting-line-height)] outline-none select-none data-[highlighted]:z-0 data-[highlighted]:text-accent-foreground data-[highlighted]:before:absolute data-[highlighted]:before:inset-x-2 data-[highlighted]:before:inset-y-0 data-[highlighted]:before:-z-1 data-[highlighted]:before:rounded-sm data-[highlighted]:before:bg-accent',
        className
      )}
      {...props}
    />
  )
}

/** Отметка выбранного пункта: стоит в отведённом ей отступе слева, вне общего потока содержимого. */
function ComboboxItemIndicator({ className, children, ...props }: ComboboxPrimitive.ItemIndicator.Props) {
  return (
    <ComboboxPrimitive.ItemIndicator
      data-slot="combobox-item-indicator"
      className={cn('absolute top-1/2 left-4 -translate-y-1/2', className)}
      {...props}
    >
      {children ?? <CheckIcon className="size-4" />}
    </ComboboxPrimitive.ItemIndicator>
  )
}

function ComboboxSeparator(props: ComboboxPrimitive.Separator.Props) {
  return <ComboboxPrimitive.Separator data-slot="combobox-separator" render={<Separator />} {...props} />
}

function ComboboxGroup({ className, ...props }: ComboboxPrimitive.Group.Props) {
  return <ComboboxPrimitive.Group data-slot="combobox-group" className={cn('mb-3 last:mb-0', className)} {...props} />
}

function ComboboxGroupLabel({ className, ...props }: ComboboxPrimitive.GroupLabel.Props) {
  return (
    <ComboboxPrimitive.GroupLabel
      data-slot="combobox-group-label"
      className={cn(
        'z-1 sticky top-0 bg-background pl-4 text-muted-foreground py-2 [font-size:var(--type-label-font-size)] [font-weight:var(--type-label-font-weight)] [line-height:var(--type-label-line-height)]',
        className
      )}
      {...props}
    />
  )
}

function ComboboxCollection(props: ComboboxPrimitive.Collection.Props) {
  return <ComboboxPrimitive.Collection data-slot="combobox-collection" {...props} />
}

export {
  Combobox,
  ComboboxArrow,
  ComboboxChip,
  ComboboxChipRemove,
  ComboboxChips,
  ComboboxClear,
  ComboboxCollection,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxGroupLabel,
  ComboboxIcon,
  ComboboxInput,
  ComboboxItem,
  ComboboxItemIndicator,
  ComboboxList,
  ComboboxPopup,
  ComboboxPositioner,
  ComboboxRow,
  ComboboxSearch,
  ComboboxSeparator,
  ComboboxStatus,
  ComboboxTrigger,
  ComboboxValue,
}
